/* BudgetCalculator — editable local-cost assumptions; owner surplus shown separately from sales. */
import React, { useEffect, useMemo, useState } from 'react';
import { IndianRupee } from 'lucide-react';

const ROWS = [
  ['sales', 'Estimated monthly sales (gross, NOT earnings)'],
  ['materials', 'Material or stock costs'],
  ['commission', 'Platform commissions (if selling online)'],
  ['overheads', 'Fuel, rent, utilities and packaging'],
  ['wastage', 'Wastage, returns and rework'],
  ['labour', 'Labour costs (incl. your helper)'],
  ['compliance', 'Compliance, licences and registrations'],
];
const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

function defaultsFor(selected, models) {
  const sc = selected?.monthlyScenario;
  const m = models?.STREET_FOOD_CART;
  const sales = sc?.grossSales?.min ?? m?.revenue?.[0] ?? 35000;
  const costs = sc?.operatingCosts?.min ?? m?.costs?.[0] ?? 27000;
  return {
    sales, materials: Math.round(costs * 0.55), commission: 0, overheads: Math.round(costs * 0.2),
    wastage: Math.round(costs * 0.1), labour: Math.round(costs * 0.1), compliance: Math.round(costs * 0.05),
  };
}

export default function BudgetCalculator({ selected, models, policy }) {
  const [v, setV] = useState(() => defaultsFor(selected, models));
  useEffect(() => { setV(defaultsFor(selected, models)); }, [selected, models]);
  const totalCosts = useMemo(() => ROWS.slice(1).reduce((a, [k]) => a + (Number(v[k]) || 0), 0), [v]);
  const surplus = (Number(v.sales) || 0) - totalCosts;
  const comps = selected?.budget?.progressionComponents;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="v20-budget">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Monthly owner-surplus calculator{selected ? ` — ${selected.title}` : ''}</p>
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3 font-semibold" data-testid="v20-budget-note">Planning estimate only—replace with at least two current local quotations.</p>
        <div className="space-y-2">
          {ROWS.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-3 text-xs text-gray-800">
              <span className="flex-1">{label}</span>
              <span className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2">
                <IndianRupee size={11} className="text-gray-400" />
                <input type="number" min="0" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="w-24 py-1.5 text-right text-sm font-semibold focus:outline-none" data-testid={`v20-budget-${k}`} />
              </span>
            </label>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-2"><p className="text-[10px] uppercase font-bold text-gray-500">Sales</p><p className="text-sm font-extrabold text-gray-900">{inr(v.sales)}</p></div>
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-2"><p className="text-[10px] uppercase font-bold text-gray-500">Total costs</p><p className="text-sm font-extrabold text-gray-900">{inr(totalCosts)}</p></div>
          <div className={`rounded-xl border p-2 ${surplus >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}><p className="text-[10px] uppercase font-bold text-gray-500">Est. owner surplus</p><p className={`text-sm font-extrabold ${surplus >= 0 ? 'text-emerald-800' : 'text-rose-800'}`} data-testid="v20-budget-surplus">{inr(surplus)}</p></div>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">{policy?.formula || 'Owner surplus = paid orders × contribution per order − fixed costs − waste − compliance.'} Owner surplus is before your own salary, interest and tax.</p>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Start-up cost components (planning ranges)</p>
        {comps?.length ? (
          <ul className="space-y-1.5 text-xs">{comps.map((c, i) => <li key={i} className="flex justify-between gap-2 border-b border-gray-100 pb-1"><span>{c.item}</span><span className="font-semibold">{c.display}</span></li>)}</ul>
        ) : selected?.starterCosts?.length ? (
          <ul className="space-y-1.5 text-xs">{selected.starterCosts.map((c, i) => <li key={i} className="flex justify-between gap-2 border-b border-gray-100 pb-1"><span>{c[0]}</span><span className="font-semibold">₹{c[1]}</span></li>)}</ul>
        ) : (
          <p className="text-xs text-gray-500 italic">Pick a business card above (Budget button) to load its cost components. Check official source for scheme-linked figures.</p>
        )}
        {selected?.monthlyScenario?.warning && <p className="text-[11px] text-gray-600 mt-3">{selected.monthlyScenario.warning}</p>}
        {selected?.planningRanges?.note && <p className="text-[11px] text-gray-600 mt-3">{selected.planningRanges.note}</p>}
        {policy?.headline && <p className="text-xs font-bold text-gray-900 mt-3">{policy.headline}</p>}
      </div>
    </div>
  );
}
